/**
 * V70G7D — 팝업 공지 실시간 감지 (프레임워크 독립 로직).
 *
 * - 변경 신호 문서(version) 를 구독하고, version 이 바뀌면 listActivePopupAnnouncements 재조회
 * - 탭 복귀/포커스/온라인 복구 시 60분 이상 지났거나 날짜·예정 시각이 지났을 때만 재조회
 * - 미래 시작 팝업(nextCheckAt) 과 KST 자정(daily/hideForDay) 에 맞춰 재조회
 * - 조회는 1건씩만 실행, 연속 요청은 병합, 최소 간격·실패 백오프로 폭증 방지
 */
import type { ActivePopupAnnouncement } from './useAnnouncements';

const KST_OFFSET_MS = 9 * 60 * 60 * 1000;
const DAY_MS = 24 * 60 * 60 * 1000;

export const POPUP_SYNC = {
  /** 연속 조회 최소 간격 */
  MIN_FETCH_GAP_MS: 5_000,
  /** 신호 수신 후 조회 분산 (150명 동시 호출 완화) */
  SIGNAL_JITTER_MS: 3_000,
  /** 예정 시작 시각 도달 후 분산 */
  START_JITTER_MS: 10_000,
  /** KST 자정 이후 분산 */
  MIDNIGHT_JITTER_MS: 60_000,
  /**
   * 탭 복귀 시 이 시간 이상 지났으면 재조회 (안전망).
   * 변경은 리스너가 감지하므로 짧게 잡으면 탭 전환마다 불필요한 조회가 생긴다.
   */
  STALE_MS: 60 * 60_000,
  /** 첫 신호 스냅샷을 기다리는 시간 (구독 실패 대비) */
  FIRST_SNAPSHOT_WAIT_MS: 3_000,
  /** 타이머 최대 대기 (브라우저 setTimeout 한계·절전 대비 재평가) */
  MAX_TIMER_MS: 6 * 60 * 60_000,
  /** 조회 실패 재시도 백오프 */
  RETRY_BASE_MS: 30_000,
  RETRY_MAX_MS: 30 * 60_000,
} as const;

export interface PopupFeedResult {
  popups: ActivePopupAnnouncement[];
  nextCheckAt: string | null;
}

export interface PopupSyncDeps {
  fetchPopups: () => Promise<PopupFeedResult>;
  /** 신호 구독. onVersion 은 스냅샷마다 호출. 반환값은 구독 해제 함수 */
  subscribe: (onVersion: (version: number) => void, onError: (e: unknown) => void) => () => void;
  onUpdate: (result: PopupFeedResult) => void;
  now: () => number;
  random: () => number;
  setTimer: (fn: () => void, ms: number) => unknown;
  clearTimer: (handle: unknown) => void;
}

export function kstDateOf(ms: number): string {
  return new Date(ms + KST_OFFSET_MS).toISOString().slice(0, 10);
}

export function nextKstMidnightMs(nowMs: number): number {
  const kst = nowMs + KST_OFFSET_MS;
  return kst - (kst % DAY_MS) + DAY_MS - KST_OFFSET_MS;
}

function parseIsoMs(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const ms = Date.parse(iso);
  return Number.isNaN(ms) ? null : ms;
}

/** 다음 깨어날 시각: 예정 시작(+분산) 과 다음 KST 자정(+분산) 중 이른 쪽 */
export function computeWakeAt(
  nowMs: number,
  nextCheckAt: string | null,
  random: () => number,
): number {
  const midnight = nextKstMidnightMs(nowMs) + Math.floor(random() * POPUP_SYNC.MIDNIGHT_JITTER_MS);
  const start = parseIsoMs(nextCheckAt);
  if (start === null || start <= nowMs) return midnight;
  return Math.min(midnight, start + Math.floor(random() * POPUP_SYNC.START_JITTER_MS));
}

/** 탭 복귀·포커스·온라인 시 재조회가 필요한지 */
export function shouldRefetchOnResume(
  nowMs: number,
  lastSuccessAt: number | null,
  nextCheckAt: string | null,
): boolean {
  if (lastSuccessAt === null) return true;
  if (nowMs - lastSuccessAt >= POPUP_SYNC.STALE_MS) return true;
  if (kstDateOf(nowMs) !== kstDateOf(lastSuccessAt)) return true;
  const start = parseIsoMs(nextCheckAt);
  return start !== null && start <= nowMs;
}

export function retryDelayMs(failCount: number): number {
  return Math.min(POPUP_SYNC.RETRY_BASE_MS * 2 ** Math.max(0, failCount - 1), POPUP_SYNC.RETRY_MAX_MS);
}

export class PopupSyncController {
  private stopped = false;
  private unsubscribe: (() => void) | null = null;
  private gotFirstSnapshot = false;
  private latestVersion: number | null = null;
  private fetchedVersion: number | null = null;
  private inflight = false;
  private pending = false;
  private lastFetchStartAt: number | null = null;
  private lastSuccessAt: number | null = null;
  private nextCheckAt: string | null = null;
  private failCount = 0;
  private fetchTimer: unknown = null;
  private fetchDueAt: number | null = null;
  private wakeTimer: unknown = null;
  private firstSnapshotTimer: unknown = null;
  /** 테스트·진단용 */
  fetchCount = 0;

  constructor(private readonly deps: PopupSyncDeps) {}

  start(): void {
    this.firstSnapshotTimer = this.deps.setTimer(() => {
      this.firstSnapshotTimer = null;
      if (!this.gotFirstSnapshot) this.requestFetch(0);
    }, POPUP_SYNC.FIRST_SNAPSHOT_WAIT_MS);
    try {
      this.unsubscribe = this.deps.subscribe(
        (v) => this.handleVersion(v),
        (e) => {
          console.warn('[popup] 변경 신호 구독 실패 (조회는 계속)', e);
          if (!this.gotFirstSnapshot && this.lastSuccessAt === null) this.requestFetch(0);
        },
      );
    } catch (e) {
      console.warn('[popup] 변경 신호 구독 실패 (조회는 계속)', e);
      this.requestFetch(0);
    }
  }

  stop(): void {
    this.stopped = true;
    this.unsubscribe?.();
    this.unsubscribe = null;
    [this.fetchTimer, this.wakeTimer, this.firstSnapshotTimer].forEach((t) => {
      if (t !== null) this.deps.clearTimer(t);
    });
    this.fetchTimer = this.wakeTimer = this.firstSnapshotTimer = null;
  }

  /** visibilitychange(visible) / focus / online */
  resume(): void {
    if (this.stopped) return;
    if (shouldRefetchOnResume(this.deps.now(), this.lastSuccessAt, this.nextCheckAt)) {
      this.requestFetch(Math.floor(this.deps.random() * 2_000));
    } else {
      this.scheduleWake();
    }
  }

  private handleVersion(version: number): void {
    if (this.stopped) return;
    const first = !this.gotFirstSnapshot;
    this.gotFirstSnapshot = true;
    this.latestVersion = version;
    if (this.firstSnapshotTimer !== null) {
      this.deps.clearTimer(this.firstSnapshotTimer);
      this.firstSnapshotTimer = null;
    }
    if (first) {
      if (this.lastSuccessAt === null || this.fetchedVersion !== version) this.requestFetch(0);
      return;
    }
    if (version === this.fetchedVersion) return;
    this.requestFetch(Math.floor(this.deps.random() * POPUP_SYNC.SIGNAL_JITTER_MS));
  }

  /** 지연 후 조회. 이미 예약돼 있으면 더 이른 쪽만 유지, 진행 중이면 완료 후 1회 재실행 */
  private requestFetch(delayMs: number): void {
    if (this.stopped) return;
    if (this.inflight) {
      this.pending = true;
      return;
    }
    const now = this.deps.now();
    const gapDue = this.lastFetchStartAt === null ? now : this.lastFetchStartAt + POPUP_SYNC.MIN_FETCH_GAP_MS;
    const due = Math.max(now + delayMs, gapDue);
    if (this.fetchTimer !== null && this.fetchDueAt !== null && this.fetchDueAt <= due) return;
    if (this.fetchTimer !== null) this.deps.clearTimer(this.fetchTimer);
    this.fetchDueAt = due;
    this.fetchTimer = this.deps.setTimer(() => {
      this.fetchTimer = null;
      this.fetchDueAt = null;
      void this.runFetch();
    }, Math.max(0, due - now));
  }

  private async runFetch(): Promise<void> {
    if (this.stopped || this.inflight) return;
    this.inflight = true;
    this.pending = false;
    this.fetchCount += 1;
    this.lastFetchStartAt = this.deps.now();
    const versionAtStart = this.latestVersion;
    try {
      const result = await this.deps.fetchPopups();
      if (this.stopped) return;
      this.failCount = 0;
      this.fetchedVersion = versionAtStart;
      this.lastSuccessAt = this.deps.now();
      this.nextCheckAt = result.nextCheckAt;
      this.deps.onUpdate(result);
      this.scheduleWake();
    } catch (e) {
      if (this.stopped) return;
      this.failCount += 1;
      console.warn('[popup] 팝업 공지 조회 실패 (무시)', e);
      this.inflight = false;
      this.requestFetch(retryDelayMs(this.failCount));
      return;
    } finally {
      this.inflight = false;
    }
    if (this.pending || (this.latestVersion !== null && this.latestVersion !== this.fetchedVersion)) {
      this.pending = false;
      this.requestFetch(Math.floor(this.deps.random() * POPUP_SYNC.SIGNAL_JITTER_MS));
    }
  }

  private scheduleWake(): void {
    if (this.stopped) return;
    if (this.wakeTimer !== null) this.deps.clearTimer(this.wakeTimer);
    const now = this.deps.now();
    const wakeAt = computeWakeAt(now, this.nextCheckAt, this.deps.random);
    const delay = Math.min(Math.max(0, wakeAt - now), POPUP_SYNC.MAX_TIMER_MS);
    this.wakeTimer = this.deps.setTimer(() => {
      this.wakeTimer = null;
      if (this.deps.now() >= wakeAt) this.requestFetch(0);
      else this.scheduleWake();
    }, delay);
  }
}

// ---------------------------------------------------------------------------
// 표시 대기열 (GlobalPopupManager)
// ---------------------------------------------------------------------------

/** 팝업 식별 키. 게임대회 공지는 'gc:' 접두로 일반 공지와 분리 (일반 공지 키는 V70G7D 와 동일) */
export function popupKey(popup: Pick<ActivePopupAnnouncement, 'id' | 'popupRevision' | 'source'>): string {
  const prefix = popup.source === 'gameCenter' ? 'gc:' : '';
  return `${prefix}${popup.id}:${popup.popupRevision}`;
}

/**
 * 여러 소스의 팝업을 하나의 대기열로 합친다.
 * 정렬: 우선순위 낮은 숫자 먼저 → 같으면 최신 작성 먼저 → 같으면 일반 공지 먼저 (안정 정렬).
 */
export function mergePopupFeeds(...feeds: ActivePopupAnnouncement[][]): ActivePopupAnnouncement[] {
  const all = feeds.flat();
  const order = new Map(all.map((p, i) => [p, i]));
  return all.slice().sort((a, b) => {
    if (a.popupPriority !== b.popupPriority) return a.popupPriority - b.popupPriority;
    const byDate = b.createdAt.localeCompare(a.createdAt);
    if (byDate !== 0) return byDate;
    return (order.get(a) ?? 0) - (order.get(b) ?? 0);
  });
}

/** 표시 완료 키. daily/hideForDay 는 KST 날짜별로 다시 표시될 수 있다 */
export function handledKey(
  popup: Pick<ActivePopupAnnouncement, 'id' | 'popupRevision' | 'popupDismissPolicy' | 'source'>,
  kstDate: string,
): string {
  const base = popupKey(popup);
  return popup.popupDismissPolicy === 'daily' || popup.popupDismissPolicy === 'hideForDay'
    ? `${base}:${kstDate}`
    : base;
}

export function isExpired(popup: Pick<ActivePopupAnnouncement, 'displayUntil'>, nowMs: number): boolean {
  const until = parseIsoMs(popup.displayUntil);
  return until !== null && until < nowMs;
}

/** 표시 대상: 만료되지 않았고 이번 화면에서 아직 처리하지 않은 팝업 (서버 정렬 유지) */
export function visiblePopups(
  popups: ActivePopupAnnouncement[],
  handled: ReadonlySet<string>,
  nowMs: number,
): ActivePopupAnnouncement[] {
  const kstDate = kstDateOf(nowMs);
  // V70G8F: 같은 공지(소스+ID)가 중복으로 들어와도 한 번만 표시·집계
  const seen = new Set<string>();
  return popups.filter((p) => {
    if (isExpired(p, nowMs) || handled.has(handledKey(p, kstDate))) return false;
    const k = popupKey(p);
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
}

/**
 * V70G8F — 진행 개수 (예: 2 / 3).
 *   completedInRun: 이번 연속 표시에서 사용자가 처리(닫기·오늘 하루 보지 않기·상세 보기)한 수
 *   queueLength   : 현재 표시 가능 대기열 수 (표시 중인 팝업 포함, 처리·만료·권한 제외분은 이미 빠짐)
 *   전체 = 처리 수 + 대기열 수, 위치 = 처리 수 + 1. 전체가 1 이하이면 표시하지 않음(null).
 *   대기 중 신규 추가·권한 제외는 대기열 길이에 바로 반영된다.
 */
export function popupProgress(
  completedInRun: number,
  queueLength: number,
): { position: number; total: number; remaining: number } | null {
  if (queueLength <= 0) return null;
  const done = Math.max(0, completedInRun);
  const total = done + queueLength;
  if (total <= 1) return null;
  return { position: done + 1, total, remaining: queueLength - 1 };
}

/** 열려 있는 팝업은 유지하고, 사라졌을 때만 다음 팝업으로 넘어간다 */
export function pickCurrent(
  visible: ActivePopupAnnouncement[],
  currentId: string | null,
): ActivePopupAnnouncement | null {
  if (currentId !== null) {
    const keep = visible.find((p) => popupKey(p) === currentId);
    if (keep) return keep;
  }
  return visible[0] ?? null;
}

/** 표시 중 팝업의 다음 만료 시각 (만료 타이머용) */
export function nextExpiryMs(popups: ActivePopupAnnouncement[], nowMs: number): number | null {
  let next: number | null = null;
  for (const p of popups) {
    const until = parseIsoMs(p.displayUntil);
    if (until === null || until < nowMs) continue;
    if (next === null || until < next) next = until;
  }
  return next;
}
